import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  api,
  type ContentSource,
  type MeResponse,
  type PageResponse,
  type ReviewStatus,
  type TreeEntry,
} from '../api/client';
import { AppHeader } from '../components/AppHeader';
import { DocumentStatusControl } from '../components/DocumentStatusControl';
import { FileHistoryPopover } from '../components/FileHistoryPopover';
import { ReviewersPopover } from '../components/ReviewersPopover';
import { useDocumentEngagement } from '../hooks/useDocumentEngagement';

const TEXT_EXTS = new Set(['md', 'markdown', 'mdx', 'html', 'htm', 'txt']);

// Highest-priority first. HTML beats markdown so a hand-authored landing page
// wins over an auto-generated README, mirroring how static site generators
// typically resolve a directory's default document.
const INDEX_PRECEDENCE = [
  'index.html',
  'index.htm',
  'index.md',
  'index.markdown',
  'index.mdx',
  'index.txt',
];

function extension(path: string): string {
  const dot = path.lastIndexOf('.');
  return dot >= 0 ? path.slice(dot + 1).toLowerCase() : '';
}

function isRenderable(entry: TreeEntry): boolean {
  return entry.type === 'blob' && TEXT_EXTS.has(extension(entry.path));
}

/**
 * Map a review status to the visual treatment used in the tree dot AND the
 * viewer toolbar badge so the two stay in sync. Colors are intentionally
 * informational only: status never gates document visibility.
 */
const REVIEW_STATUS_PRESENTATION: Record<
  ReviewStatus,
  { label: string; dotClass: string }
> = {
  draft: { label: 'Draft', dotClass: 'bg-wc-muted' },
  in_review: { label: 'In review', dotClass: 'bg-amber-500' },
  approved: { label: 'Approved', dotClass: 'bg-emerald-500' },
};

export function reviewStatusLabel(status: ReviewStatus): string {
  return REVIEW_STATUS_PRESENTATION[status].label;
}

function reviewStatusDotClass(status: ReviewStatus): string {
  return REVIEW_STATUS_PRESENTATION[status].dotClass;
}

function findIndexEntry(entries: TreeEntry[]): TreeEntry | null {
  for (const candidate of INDEX_PRECEDENCE) {
    const match = entries.find(
      (e) => e.type === 'blob' && e.name.toLowerCase() === candidate,
    );
    if (match) return match;
  }
  return null;
}

const PROXY_PATH_PREFIX = '/api/v1/content/proxy/';

/**
 * Read the path captured by the `/portal/*` splat. React Router already URL-
 * decodes splat segments, so the returned value is the same string that should
 * be used as a content path (e.g. "docs/intro.md").
 */
function useUrlPath(): string {
  const params = useParams();
  return params['*'] ?? '';
}

/** Build a portal URL for a content path. Encodes each segment so spaces /
 *  unicode / pluses don't break the splat round-trip, while keeping slashes. */
function toPortalUrl(path: string): string {
  if (!path) return '/portal';
  return `/portal/${path.split('/').map(encodeURIComponent).join('/')}`;
}

/** Same encoding rule as toPortalUrl, but emits the cached-content proxy URL. */
function toProxyUrl(path: string): string {
  if (!path) return PROXY_PATH_PREFIX;
  return `${PROXY_PATH_PREFIX}${path.split('/').map(encodeURIComponent).join('/')}`;
}

export interface ProxyLinkTarget {
  /** Decoded content path relative to the cache root (e.g. "docs/report.pdf"). */
  path: string;
  /**
   * True when the portal renders this target inline (HTML / markdown / text)
   * or when it has no file extension (a directory or extension-less page that
   * the iframe can navigate to itself). False only for real, extension-bearing
   * non-text files (PDFs, images, archives, Office docs...) that the sandboxed
   * iframe cannot display and that must be opened through the portal instead.
   */
  renderable: boolean;
}

/**
 * Classify an anchor's `href` against the same-origin content proxy.
 *
 * Returns the decoded content path plus whether the portal can render it
 * inline, or `null` when the link does not point at this origin's content
 * proxy (external sites, in-page `#` anchors, `mailto:`, the SPA's own routes,
 * etc.). Used to intercept in-iframe clicks on files the sandboxed preview
 * cannot show (most importantly PDFs) and reroute them through the portal.
 */
export function classifyProxyLink(href: string, origin: string): ProxyLinkTarget | null {
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  if (!url.pathname.startsWith(PROXY_PATH_PREFIX)) return null;
  const encoded = url.pathname.slice(PROXY_PATH_PREFIX.length);
  if (!encoded) return null;
  let path: string;
  try {
    path = encoded.split('/').map(decodeURIComponent).join('/');
  } catch {
    return null;
  }
  const ext = extension(path);
  return { path, renderable: ext === '' || TEXT_EXTS.has(ext) };
}

/**
 * Shared gate for in-iframe link clicks. Resolves the clicked element's nearest
 * anchor and, when it targets a non-renderable proxy file, returns that path so
 * the caller can open it outside the sandboxed iframe. Returns `null` (leaving
 * the browser's default behaviour intact) for modified clicks, non-anchor
 * targets, HTML/extension-less links, and links that leave the content proxy.
 */
function proxyFileFromClick(event: MouseEvent, origin: string): string | null {
  if (event.defaultPrevented) return null;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return null;
  }
  const target = event.target as Element | null;
  const anchor = target?.closest?.('a');
  if (!anchor?.href) return null;
  const link = classifyProxyLink(anchor.href, origin);
  if (!link || link.renderable) return null;
  return link.path;
}

/**
 * Treat a splat path as a file when its last segment has an extension, else
 * as a folder. Lets us interpret a deep link like `/portal/docs/intro.md`
 * without first round-tripping to the server. A folder named `v1.0.0` would
 * be misread as a file here, but the tree query will surface the mistake.
 */
function isLikelyFilePath(path: string): boolean {
  if (!path) return false;
  const lastSlash = path.lastIndexOf('/');
  const lastDot = path.lastIndexOf('.');
  return lastDot > lastSlash;
}

function parentOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i >= 0 ? path.slice(0, i) : '';
}

function basenameOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i >= 0 ? path.slice(i + 1) : path;
}

/** Redirect to the public login page, preserving the current portal URL so we
 *  can resume there after a successful sign-in. */
function redirectToLogin(): void {
  const next = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/?returnTo=${encodeURIComponent(next)}`);
}

export function PortalPage() {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
  // Source query is gated on the picker step: until a global user has
  // chosen an active customer their `customerId` still points at their
  // home tenant, but UX-wise we should not start pulling content yet --
  // we'd just throw the response away once they redirect to the picker.
  const needsPicker =
    !!meQuery.data && meQuery.data.isGlobalUser && !meQuery.data.hasPickedActiveCustomer;
  const sourceQuery = useQuery({
    queryKey: ['content-source'],
    queryFn: api.source,
    enabled: !!meQuery.data && !needsPicker,
    retry: false,
    // Re-poll while the cache is being populated so the portal flips to the
    // real view as soon as the first refresh completes.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data && 'cacheStatus' in data && data.cacheStatus === 'fetching') return 2000;
      return false;
    },
  });

  if (meQuery.isLoading || (meQuery.data && !needsPicker && sourceQuery.isLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-wc-gray">
        <p className="text-sm text-wc-muted">Loading…</p>
      </div>
    );
  }
  if (meQuery.isError || !meQuery.data) {
    redirectToLogin();
    return null;
  }
  if (needsPicker) {
    return <Navigate to="/portal/select" replace />;
  }
  if (sourceQuery.isError || !sourceQuery.data) {
    return (
      <div className="flex h-screen flex-col bg-wc-white">
        <AppHeader me={meQuery.data} />
        <main className="flex flex-1 items-center justify-center px-6 py-8">
          <p className="text-sm text-wc-red">Failed to load content source.</p>
        </main>
      </div>
    );
  }

  const me = meQuery.data;
  const source = sourceQuery.data;

  if (source.mode === 'unconfigured') {
    return (
      <PortalMessage
        me={me}
        title="No content yet"
        body="Your portal is set up, but a content source hasn't been configured. Please contact your Weigandt contact to publish your documents."
      />
    );
  }

  // The cache is still warming up. Don't try to render the tree / iframe yet —
  // we'd just hit 503s. The polling refetch above flips us out of this state.
  if (source.cacheStatus === 'fetching' || source.cacheStatus === 'empty') {
    return (
      <PortalMessage
        me={me}
        title="Preparing your portal…"
        body="We're pulling the latest content from your GitLab repository. This usually takes a few seconds. The page will refresh automatically."
      />
    );
  }

  if (source.cacheStatus === 'error') {
    return (
      <PortalMessage
        me={me}
        title="Couldn't load your content"
        body="The latest refresh from GitLab failed. Please contact your Weigandt contact so they can investigate."
      />
    );
  }

  if (source.mode === 'pages') {
    return <PagesPortal me={me} source={source} />;
  }

  return <RepoPortal me={me} />;
}

interface PortalMessageProps {
  me: MeResponse;
  title: string;
  body: string;
}

function PortalMessage({ me, title, body }: PortalMessageProps) {
  return (
    <div className="flex h-screen flex-col bg-wc-white">
      <AppHeader me={me} />
      <main className="flex flex-1 items-center justify-center bg-wc-gray px-6 py-8">
        <div className="card max-w-lg p-10 text-center">
          <h1 className="font-serif text-2xl text-wc-black">{title}</h1>
          <p className="mt-3 text-sm text-wc-muted-strong">{body}</p>
        </div>
      </main>
    </div>
  );
}

interface PagesPortalProps {
  me: MeResponse;
  // source.entryUrl is the default Pages root. The iframe src is derived
  // from the URL splat instead so deep links reach a specific page.
  source: Extract<ContentSource, { mode: 'pages' }>;
}

function PagesPortal({ me, source: _source }: PagesPortalProps) {
  const navigate = useNavigate();
  const urlPath = useUrlPath();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Engagement key follows the URL splat: each in-iframe navigation rewrites
  // the outer URL via handleLoad, which re-keys the hook and starts a fresh
  // engagement window for the new document.
  useDocumentEngagement(urlPath || '/');
  // Tracks what the iframe is currently displaying. Updated both when the
  // outer URL drives a programmatic src change and when an in-iframe link
  // click triggers our load handler. We use a ref (not state) because reading
  // it during render would force iframe src updates on every render.
  const iframeShownPath = useRef<string>(urlPath);
  // Initial src for the first paint. Fixed at mount time so subsequent
  // re-renders never let React rewrite the DOM `src` attribute — we manage
  // navigation imperatively via the ref below to avoid spurious reloads when
  // the outer URL update is just mirroring a navigation the iframe already did.
  const initialSrcRef = useRef<string>(toProxyUrl(urlPath));

  // External-source URL changes (browser back/forward, deep link, or a portal
  // navigation that isn't from inside the iframe) update the iframe imperatively.
  // If the iframe is already at the requested path we skip the write so the
  // page doesn't reload mid-browse.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    if (iframeShownPath.current === urlPath) return;
    iframe.src = toProxyUrl(urlPath);
    iframeShownPath.current = urlPath;
  }, [urlPath]);

  const handleLoad = (): void => {
    const iframe = iframeRef.current;
    try {
      const win = iframe?.contentWindow;
      const doc = iframe?.contentDocument;
      // Pages mode has no sidebar/file viewer, so non-renderable links (PDFs
      // etc.) that the sandboxed iframe can't display are opened in a new tab,
      // where the browser renders them outside the sandbox.
      if (doc && win) {
        const origin = win.location.origin;
        doc.addEventListener(
          'click',
          (event) => {
            const path = proxyFileFromClick(event, origin);
            if (path === null) return;
            event.preventDefault();
            window.open(toProxyUrl(path), '_blank', 'noopener,noreferrer');
          },
          true,
        );
      }
      const pathname = win?.location.pathname;
      if (!pathname || !pathname.startsWith(PROXY_PATH_PREFIX)) return;
      const encoded = pathname.slice(PROXY_PATH_PREFIX.length);
      const decoded = encoded.split('/').map(decodeURIComponent).join('/');
      iframeShownPath.current = decoded;
      if (decoded === urlPath) return;
      navigate(toPortalUrl(decoded), { replace: true });
    } catch {
      // Cross-origin or other access error — leave the outer URL alone.
    }
  };

  return (
    <div className="flex h-screen flex-col bg-wc-white">
      <AppHeader me={me} />
      <main className="flex-1 overflow-hidden bg-wc-white">
        <iframe
          ref={iframeRef}
          onLoad={handleLoad}
          title="Customer documentation"
          src={initialSrcRef.current}
          className="block h-full w-full border-0 bg-wc-white"
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
          referrerPolicy="no-referrer"
        />
      </main>
    </div>
  );
}

interface RepoPortalProps {
  me: MeResponse;
}

function RepoPortal({ me }: RepoPortalProps) {
  const navigate = useNavigate();
  const urlPath = useUrlPath();

  const [currentDir, setCurrentDir] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<TreeEntry | null>(null);
  // Tracks what the iframe is currently displaying after the user follows
  // internal links inside the proxied HTML. Drives sidebar highlight +
  // auto-reveal, but deliberately NOT pageQuery / selectedFile — those would
  // unmount the iframe mid-browse and lose scroll position.
  const [iframeNavPath, setIframeNavPath] = useState<string | null>(null);
  // Monotonic counter used as the HTML iframe's `key` so we can force a
  // remount in the narrow case where the user re-clicks the already-selected
  // file in the sidebar after the iframe has wandered via an in-iframe link.
  // In that case selectedFile / pageQuery are unchanged, so React wouldn't
  // otherwise rewrite the iframe `src` attribute and the stale page would
  // stay on screen.
  const [iframeReloadKey, setIframeReloadKey] = useState<number>(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('cp.portal.nav.collapsed') === '1';
    } catch {
      return false;
    }
  });

  // URL is the source of truth: whenever the splat changes (back/forward,
  // direct deep-link, sidebar click that called navigate()), reconcile the
  // local state from it. The early-out keeps in-iframe nav from bouncing —
  // when the iframe just synced the URL to its own location via navigate()
  // we don't want to throw away `iframeNavPath` and reload the iframe.
  useEffect(() => {
    if (iframeNavPath === urlPath) return;
    if (selectedFile?.path === urlPath) return;
    if (!urlPath) {
      setCurrentDir('');
      setSelectedFile(null);
      setIframeNavPath(null);
      return;
    }
    if (isLikelyFilePath(urlPath)) {
      setCurrentDir(parentOf(urlPath));
      setSelectedFile({ name: basenameOf(urlPath), type: 'blob', path: urlPath });
      setIframeNavPath(null);
      return;
    }
    setCurrentDir(urlPath);
    setSelectedFile(null);
    setIframeNavPath(null);
  }, [urlPath, iframeNavPath, selectedFile]);

  useEffect(() => {
    try {
      localStorage.setItem('cp.portal.nav.collapsed', sidebarCollapsed ? '1' : '0');
    } catch {
      // ignore (private mode etc.)
    }
  }, [sidebarCollapsed]);

  const treeQuery = useQuery({
    queryKey: ['tree', currentDir],
    queryFn: () => api.tree(currentDir),
  });

  // Auto-open the folder's index file (index.html / .md / etc.) whenever the
  // user navigates into a folder that has one and has no manual file pick yet.
  // Skip when the iframe is the source of truth (an in-iframe link landed us
  // here) so we don't yank the user away from the page they're already viewing.
  useEffect(() => {
    if (selectedFile) return;
    if (iframeNavPath) return;
    if (!treeQuery.data) return;
    // Defensive: treeQuery.data could be stale from a previous currentDir
    // during a fast switch. The server echoes `path` in the response.
    if (treeQuery.data.path !== currentDir) return;
    const index = findIndexEntry(treeQuery.data.entries);
    if (index && isRenderable(index)) {
      // `replace` keeps the URL upgrade from "folder" to "index file" out of
      // the back-history so the user doesn't have to click back twice to leave
      // the folder.
      navigate(toPortalUrl(index.path), { replace: true });
    }
  }, [currentDir, selectedFile, treeQuery.data, iframeNavPath, navigate]);

  const pageQuery = useQuery({
    queryKey: ['page', selectedFile?.path ?? null],
    queryFn: () => api.page(selectedFile!.path),
    enabled: !!selectedFile && isRenderable(selectedFile),
  });

  const parentDir = currentDir.includes('/')
    ? currentDir.slice(0, currentDir.lastIndexOf('/'))
    : '';
  // Highlight reflects what's actually on screen — including pages the user
  // reached by clicking links inside the iframe, not just the last sidebar pick.
  const highlightPath = iframeNavPath ?? selectedFile?.path;

  const onEntryClick = (entry: TreeEntry) => {
    // Re-clicking the currently selected file after the iframe has wandered
    // via an in-iframe link: neither selectedFile nor pageQuery will change,
    // so the declarative `src` binding won't re-apply. Reset the wander state
    // (so the highlight snaps back immediately) and bump the iframe key to
    // force React to remount it and reload `pageQuery.data.iframeUrl`.
    if (
      entry.path === selectedFile?.path &&
      iframeNavPath !== null &&
      iframeNavPath !== entry.path
    ) {
      setIframeNavPath(null);
      setIframeReloadKey((n) => n + 1);
    }
    navigate(toPortalUrl(entry.path));
  };

  // Wraps the iframe load handler so an in-iframe link click both updates
  // the highlight (iframeNavPath) and synchronises the outer URL — making
  // the address bar shareable mid-browse. We use `replace` so the iframe's
  // own browser history entry stays the single source of "back" semantics.
  const onIframeNavigate = (path: string | null) => {
    setIframeNavPath(path);
    if (path && path !== urlPath) {
      navigate(toPortalUrl(path), { replace: true });
    }
  };

  // A link inside the HTML preview pointed at a file the iframe can't render
  // (typically a PDF). Clear the in-iframe wander state and navigate to the
  // file like a sidebar click would, so the URL effect swaps `selectedFile`
  // and the viewer renders it via the <object>/download path.
  const onOpenContentPath = (path: string) => {
    setIframeNavPath(null);
    navigate(toPortalUrl(path));
  };

  return (
    <div className="flex h-screen flex-col bg-wc-white">
      <AppHeader me={me} />
      <main className="flex flex-1 overflow-hidden">
        <aside
          className={`relative flex shrink-0 flex-col overflow-hidden border-r border-wc-muted/20 bg-wc-white transition-[width] duration-200 ease-out ${
            sidebarCollapsed ? 'w-12' : 'w-72'
          }`}
          aria-label="Documentation tree"
        >
          <div className="flex h-14 items-center border-b border-wc-muted/15 px-2">
            <button
              type="button"
              className="ml-auto rounded p-1.5 text-wc-muted-strong transition-colors hover:bg-wc-gray/60 hover:text-wc-blue"
              onClick={() => setSidebarCollapsed((c) => !c)}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!sidebarCollapsed}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronIcon direction={sidebarCollapsed ? 'right' : 'left'} />
            </button>
          </div>

          {/* The tree content stays mounted (so re-expanding is instant) but is
              hidden + clipped while collapsed so the rail stays narrow. */}
          <div
            className={`flex flex-1 flex-col overflow-y-auto transition-opacity duration-150 ${
              sidebarCollapsed ? 'pointer-events-none invisible opacity-0' : 'opacity-100'
            }`}
            aria-hidden={sidebarCollapsed}
          >
            <div className="border-b border-wc-muted/15 px-4 pb-3 pt-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-wc-muted-strong">
                Documentation
              </p>
              <div className="mt-2 flex items-center gap-2">
                {currentDir ? (
                  <button
                    type="button"
                    className="-ml-1 inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-wc-blue hover:bg-wc-blue/10"
                    onClick={() => navigate(toPortalUrl(parentDir))}
                    title="Back to parent folder"
                    aria-label="Back to parent folder"
                  >
                    <ArrowLeftIcon />
                    Back
                  </button>
                ) : null}
                <p
                  className="min-w-0 flex-1 truncate text-sm font-medium text-wc-black"
                  title={currentDir || 'Root'}
                >
                  {currentDir
                    ? currentDir
                        .split('/')
                        .map((seg, i, arr) => (
                          <span key={i}>
                            {i > 0 && <span className="mx-1 text-wc-muted">/</span>}
                            <span className={i === arr.length - 1 ? 'text-wc-black' : 'text-wc-muted-strong'}>
                              {seg}
                            </span>
                          </span>
                        ))
                    : 'Root'}
                </p>
              </div>
            </div>

            {treeQuery.isLoading ? (
              <div className="space-y-2 p-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-7 w-full animate-pulse rounded bg-wc-gray/70" />
                ))}
              </div>
            ) : treeQuery.isError ? (
              <p className="p-4 text-sm text-wc-red">Failed to load content tree.</p>
            ) : treeQuery.data?.entries.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-wc-muted">
                This folder is empty.
              </p>
            ) : (
              <ul className="space-y-0.5 p-2">
                {treeQuery.data?.entries.map((entry) => {
                  const isSelected = entry.path === highlightPath;
                  const isFolder = entry.type === 'tree';
                  const ext = isFolder ? null : extension(entry.name);
                  const labelName = isFolder
                    ? entry.name
                    : ext
                      ? entry.name.slice(0, -(ext.length + 1))
                      : entry.name;
                  // Default to in_review when the server omits the status
                  // (legacy response or a brand-new file before its row exists).
                  const reviewStatus: ReviewStatus = !isFolder
                    ? (entry.reviewStatus ?? 'in_review')
                    : 'in_review';
                  // Files matching the admin's exclusion patterns are not
                  // reviewable; suppress the dot AND the status tooltip
                  // suffix so the tree row reads as "just a file".
                  const showReviewDot =
                    !isFolder && entry.reviewable !== false;
                  return (
                    <li key={entry.path}>
                      <button
                        type="button"
                        tabIndex={sidebarCollapsed ? -1 : 0}
                        onClick={() => onEntryClick(entry)}
                        title={
                          isFolder || !showReviewDot
                            ? entry.name
                            : `${entry.name} — ${reviewStatusLabel(reviewStatus)}`
                        }
                        className={`group relative flex w-full items-center gap-2 rounded-md py-1.5 pl-3 pr-2 text-left text-sm transition-colors ${
                          isSelected
                            ? 'bg-wc-blue/10 font-medium text-wc-blue'
                            : 'text-wc-black hover:bg-wc-gray/70'
                        }`}
                        aria-current={isSelected ? 'true' : undefined}
                      >
                        {isSelected && (
                          <span
                            aria-hidden
                            className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-wc-blue"
                          />
                        )}
                        <span
                          aria-hidden
                          className={`flex h-5 w-5 shrink-0 items-center justify-center ${
                            isSelected
                              ? 'text-wc-blue'
                              : isFolder
                                ? 'text-wc-blue/80'
                                : 'text-wc-muted-strong'
                          }`}
                        >
                          {isFolder ? <FolderIcon /> : <FileIcon ext={ext} />}
                        </span>
                        {showReviewDot && (
                          <span
                            aria-label={`Status: ${reviewStatusLabel(reviewStatus)}`}
                            className={`h-2 w-2 shrink-0 rounded-full ${reviewStatusDotClass(reviewStatus)}`}
                          />
                        )}
                        <span className="min-w-0 flex-1 truncate">{labelName}</span>
                        {isFolder ? (
                          <span
                            aria-hidden
                            className={`shrink-0 transition-opacity ${
                              isSelected ? 'text-wc-blue' : 'text-wc-muted opacity-0 group-hover:opacity-100'
                            }`}
                          >
                            <ChevronRightIcon />
                          </span>
                        ) : ext ? (
                          <span
                            aria-hidden
                            className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                              isSelected
                                ? 'bg-wc-blue/15 text-wc-blue'
                                : 'bg-wc-gray text-wc-muted-strong'
                            }`}
                          >
                            {ext}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        <section className="flex min-w-0 flex-1 flex-col overflow-auto bg-wc-white">
          <Viewer
            selectedFile={selectedFile}
            pageQuery={pageQuery}
            iframeNavPath={iframeNavPath}
            iframeReloadKey={iframeReloadKey}
            onIframeNavigate={onIframeNavigate}
            onOpenContentPath={onOpenContentPath}
            canViewAuditDashboard={me.canViewAuditDashboard}
          />
        </section>
      </main>
    </div>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === 'left' ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      fillOpacity={0.15}
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function FileIcon({ ext }: { ext: string | null }) {
  // Show a small accent corner on the icon based on file type. Subtle but
  // gives a glance-able sense of what's text vs binary vs image.
  const tone = fileTone(ext);
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <polyline points="14 3 14 9 20 9" fill={tone === 'doc' ? 'currentColor' : 'none'} fillOpacity={0.25} />
      {tone === 'text' && <line x1="8" y1="13" x2="16" y2="13" />}
      {tone === 'text' && <line x1="8" y1="17" x2="14" y2="17" />}
      {tone === 'image' && <circle cx="10" cy="14" r="1.5" />}
      {tone === 'image' && <polyline points="8 18 12 14 14 16 17 13 17 18" />}
    </svg>
  );
}

function fileTone(ext: string | null): 'text' | 'image' | 'doc' | 'binary' {
  if (!ext) return 'binary';
  if (['md', 'markdown', 'mdx', 'txt', 'html', 'htm'].includes(ext)) return 'text';
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
  if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return 'doc';
  return 'binary';
}

interface ViewerProps {
  selectedFile: TreeEntry | null;
  pageQuery: ReturnType<typeof useQuery<PageResponse>>;
  iframeNavPath: string | null;
  /**
   * Bumped by the parent to force the HTML iframe to remount and re-load
   * `pageQuery.data.iframeUrl` in the edge case where neither `selectedFile`
   * nor `pageQuery.data` changes (e.g. the user re-clicks the currently
   * selected file in the sidebar after following an in-iframe link).
   */
  iframeReloadKey: number;
  onIframeNavigate: (path: string | null) => void;
  /**
   * Open a content file that the sandboxed preview cannot render itself (e.g. a
   * PDF linked from inside an HTML page). The parent reroutes it through the
   * portal's own file viewer so the experience matches a sidebar click.
   */
  onOpenContentPath: (path: string) => void;
  /**
   * When true, the toolbar renders the per-file "History" popover. Mirrors
   * the server-side `DashboardAccessGuard`: only portal users granted audit
   * access by a platform admin should see who else has opened a file.
   */
  canViewAuditDashboard: boolean;
}

function Viewer({
  selectedFile,
  pageQuery,
  iframeNavPath,
  iframeReloadKey,
  onIframeNavigate,
  onOpenContentPath,
  canViewAuditDashboard,
}: ViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleIframeLoad = (): void => {
    const iframe = iframeRef.current;
    try {
      // Same-origin (Nest serves the proxy) + `allow-same-origin` sandbox makes
      // `contentWindow.location` and `contentDocument` readable from the parent.
      const win = iframe?.contentWindow;
      const doc = iframe?.contentDocument;
      const pathname = win?.location.pathname;
      if (!pathname || !pathname.startsWith(PROXY_PATH_PREFIX)) {
        onIframeNavigate(null);
      } else {
        const encoded = pathname.slice(PROXY_PATH_PREFIX.length);
        const decoded = encoded.split('/').map(decodeURIComponent).join('/');
        onIframeNavigate(decoded);
      }
      // Links to non-renderable files (PDFs, images, archives...) cannot be
      // shown by the sandboxed iframe — a same-frame navigation to them is
      // silently blocked. Intercept those clicks and hand the file to the
      // portal viewer so they behave exactly like a sidebar pick. Captured so
      // we win over the page's own handlers; re-bound per load since each
      // navigation yields a fresh document.
      if (doc && win) {
        const origin = win.location.origin;
        doc.addEventListener(
          'click',
          (event) => {
            const path = proxyFileFromClick(event, origin);
            if (path === null) return;
            event.preventDefault();
            onOpenContentPath(path);
          },
          true,
        );
      }
    } catch {
      // Cross-origin or other access error — fall back to selectedFile.path.
      onIframeNavigate(null);
    }
  };

  // Derive the currently-visible path defensively so we can call useQuery at
  // the top of the component (rules of hooks). visiblePath is '' until a
  // file is picked; the query is gated by `enabled` so an empty path never
  // hits the network.
  const isHtmlPage = pageQuery.data?.type === 'html';
  const visiblePath = iframeNavPath ?? selectedFile?.path ?? '';
  const visibleExt = extension(visiblePath);
  const visibleIsHtml = visibleExt === 'html' || visibleExt === 'htm';

  // Track engagement time on whichever document is currently visible. Null
  // when there is no selection so we don't open a row for the empty state.
  useDocumentEngagement(visiblePath || null);

  // Per-customer admin allowlist decides which HTML pages get a PDF FAB.
  // Cached aggressively because the patterns change rarely; re-keys on path
  // so following an in-iframe link re-checks against the new file.
  const pdfAllowedQuery = useQuery({
    queryKey: ['pdf-allowed', visiblePath],
    queryFn: () => api.pdfAllowed(visiblePath),
    enabled: !!visiblePath && isHtmlPage && visibleIsHtml,
    staleTime: 5 * 60_000,
  });
  const showPdfFab = isHtmlPage && visibleIsHtml && pdfAllowedQuery.data?.allowed === true;

  if (!selectedFile) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <h2 className="font-serif text-2xl text-wc-black">Pick a document</h2>
        <p className="mt-3 max-w-md text-sm text-wc-muted-strong">
          Choose a file from the tree on the left. Markdown and HTML pages are
          rendered inline; other files are streamed as downloads.
        </p>
      </div>
    );
  }

  if (!isRenderable(selectedFile)) {
    const url = api.fileUrl(selectedFile.path);
    const ext = extension(selectedFile.path);
    if (ext === 'pdf') {
      return (
        <div className="flex flex-1 flex-col">
          <ViewerHeader>
            <DocumentStatusControl path={selectedFile.path} />
            <ReviewersPopover path={selectedFile.path} />
            {canViewAuditDashboard && <FileHistoryPopover path={selectedFile.path} />}
            <ToolbarLink href={url} download>
              Download
            </ToolbarLink>
          </ViewerHeader>
          {/* PDFs are viewable in-browser via <object>; the explicit Download
              button above is always reachable even when the embedded viewer
              renders correctly (the inner fallback only shows if the browser
              has no PDF plug-in). */}
          <object data={url} type="application/pdf" className="block flex-1 w-full">
            <div className="p-6">
              <a className="btn-secondary" href={url} download>
                Download {selectedFile.name}
              </a>
            </div>
          </object>
        </div>
      );
    }
    return (
      <div className="flex flex-1 flex-col">
        <ViewerHeader>
          <DocumentStatusControl path={selectedFile.path} />
          <ReviewersPopover path={selectedFile.path} />
          {canViewAuditDashboard && <FileHistoryPopover path={selectedFile.path} />}
        </ViewerHeader>
        <div className="p-6">
          <a className="btn-primary" href={url} download>
            Download {selectedFile.name}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      {visiblePath && (
        <ViewerHeader>
          <DocumentStatusControl path={visiblePath} />
          <ReviewersPopover path={visiblePath} />
          {canViewAuditDashboard && <FileHistoryPopover path={visiblePath} />}
        </ViewerHeader>
      )}
      {pageQuery.isLoading ? (
        <p className="p-6 text-sm text-wc-muted">Rendering…</p>
      ) : pageQuery.isError || !pageQuery.data ? (
        <p className="p-6 text-sm text-wc-red">Failed to render this page.</p>
      ) : pageQuery.data.type === 'html' ? (
        // HTML files render in a sandboxed iframe so their <style>, <link>,
        // and inline style attributes survive intact and don't bleed into the
        // surrounding portal chrome. The iframe is same-origin (the proxy is
        // served by Nest), so the session cookie reaches it and any sibling
        // assets the page references get served from the cache too.
        <div className="relative flex w-full flex-1">
          <iframe
            key={iframeReloadKey}
            ref={iframeRef}
            onLoad={handleIframeLoad}
            title={pageQuery.data.path}
            src={pageQuery.data.iframeUrl}
            className="block w-full flex-1 border-0 bg-wc-white"
            sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
            referrerPolicy="no-referrer"
          />
          {showPdfFab && (
            <a
              href={api.pdfUrl(visiblePath)}
              download
              className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full bg-wc-blue px-4 py-2 text-sm font-medium text-wc-white shadow-lg shadow-wc-blue/30 transition-transform hover:bg-wc-blue-dark hover:scale-105"
              title="Download this page as PDF"
              aria-label="Download this page as PDF"
            >
              <DownloadIcon />
              PDF
            </a>
          )}
        </div>
      ) : (
        <article
          className="prose prose-slate max-w-none px-8 py-6
            prose-headings:font-serif prose-headings:text-wc-blue
            prose-a:text-wc-blue prose-a:underline-offset-4
            prose-code:rounded prose-code:bg-wc-gray prose-code:px-1.5 prose-code:py-0.5"
          dangerouslySetInnerHTML={{ __html: pageQuery.data.html }}
        />
      )}
    </div>
  );
}

interface ViewerHeaderProps {
  children?: ReactNode;
}

function ViewerHeader({ children }: ViewerHeaderProps) {
  if (!children) return null;
  return (
    <div className="flex h-14 items-center justify-end gap-2 border-b border-wc-muted/15 px-6">
      {children}
    </div>
  );
}

interface ToolbarLinkProps {
  href: string;
  download?: boolean;
  children: ReactNode;
}

function ToolbarLink({ href, download, children }: ToolbarLinkProps) {
  return (
    <a
      className="inline-flex items-center gap-1.5 rounded-md bg-wc-blue px-3 py-1.5 text-xs font-medium text-wc-white transition-colors hover:bg-wc-blue-dark"
      href={href}
      download={download}
    >
      <DownloadIcon />
      {children}
    </a>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
